import {
  onlineManager,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type InvalidateQueryFilters,
} from "@tanstack/react-query";
import { omit } from "es-toolkit";
import { useCallback, useEffect, useEffectEvent, useMemo } from "react";

import {
  type InteractModuleListItem,
  type InteractModuleResource,
  type InteractModuleResourceProperties,
  type ModuleOpenUser,
  type UpdateModuleSessionOptions,
} from "@skilitics/nautilus-endpoint-definition";

import { isFetchOrAxiosNetworkError } from "../../../errors/isFetchOrAxiosNetworkError.ts";
import { useLandingPageSettings } from "../../LandingPageSettingsContext.ts";
import { sessionId } from "./sessionId.ts";
import { useHeartbeat } from "./useHeartbeat.ts";
import { ModuleSessionConflict, useModuleStore, type ModuleStoreApi } from "./useModuleStore.ts";

type MutationOptions = {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
};

const BASE_QUERY_KEY = ["modules"];

// we can invalidate the module list query cache for all filters on it with this when
// a module is updated, added, or removed
// see https://tanstack.com/query/v4/docs/framework/react/guides/query-invalidation
const invalidateModuleListQueryOptions: InvalidateQueryFilters = {
  queryKey: BASE_QUERY_KEY,
  refetchType: "all",
};

export const useFetchModuleList = () => {
  const { getAll } = useModuleStore();
  const { devFetchAllUsersModules } = useLandingPageSettings();

  const { data, status, isFetching } = useQuery({
    queryKey: [...BASE_QUERY_KEY, "all-users", devFetchAllUsersModules],
    queryFn: async () =>
      getAll({
        createdBy: devFetchAllUsersModules ? "all" : undefined,
        sessionId,
      }),
    retry: 3,
  });

  return {
    modules: data?.items,
    createAccess: data?.createAccess,
    status,
    isFetching,
  };
};

const getModuleQueryOptions = (api: ModuleStoreApi, moduleId: string) =>
  queryOptions({
    queryKey: ["module", moduleId],
    queryFn: async () => {
      const module = await api.get(moduleId);
      return stripTrackingAttributes(module);
    },
    retry: false,
  });

export const useModuleTitle = (moduleId: string) => {
  const api = useModuleStore();

  const { data } = useQuery(getModuleQueryOptions(api, moduleId));
  return data?.title;
};

export type ActiveModuleResult =
  | ActiveModuleResultPending
  | ActiveModuleResultSuccess
  | ActiveModuleResultConflict
  | ActiveModuleResultError;

export interface ActiveModuleResultPending {
  status: "pending";
}

export interface ActiveModuleResultSuccess {
  status: "success";
  module: InteractModuleResourceProperties;
}

export interface ActiveModuleResultConflict {
  status: "conflict";
  openUser: ModuleOpenUser | null;
  forceOpen?: () => void;
}

export interface ActiveModuleResultError {
  status: "error";
  error: unknown;
  retry?: () => void;
}

export const useActiveModule = (moduleId: InteractModuleResourceProperties["id"]): ActiveModuleResult => {
  const api = useModuleStore();

  const { data = null, status, error } = useQuery(getModuleQueryOptions(api, moduleId));

  const session = useModuleSession(api, data);

  if (status === "pending" || session.state === "pending") {
    return { status: "pending" };
  }

  if (status === "error" || data === null) {
    return { status: "error", error };
  }

  if (session.state === "conflict") {
    const { openUser, retry } = session;
    return {
      status: "conflict",
      openUser,
      forceOpen: openUser?.isYou ? () => void retry({ force: true }) : undefined,
    };
  }

  if (session.state === "error") {
    return {
      status: "error",
      error: session.error,
      retry: () => void session.retry(),
    };
  }

  return { status: "success", module: data };
};

export type ModuleSessionState =
  // Module not available (not loaded, locked)
  | "disabled"
  // Waiting for session to be established
  | "pending"
  // We have the session
  | "active"
  // Another user has the session
  | "conflict"
  // Some other error occurred
  | "error";

export interface ModuleSession {
  state: ModuleSessionState;
  openUser: ModuleOpenUser | null;
  error: unknown;
  retry: (options?: UpdateModuleSessionOptions) => void;
}

// Disregard module.alreadyOpenUser as we want the session response to be the source of truth
const useModuleSession = (api: ModuleStoreApi, module: InteractModuleResourceProperties | null): ModuleSession => {
  const available = module && !module.readonly;
  const moduleId = module?.id;

  const request = useCallback(
    async (_requestOptions: unknown, retryOptions?: UpdateModuleSessionOptions) => {
      await api.updateSession(moduleId!, sessionId, {
        lockSeconds: 60,
        ...retryOptions,
      });
    },
    [api, moduleId],
  );

  const { state, error, retry } = useHeartbeat<UpdateModuleSessionOptions | undefined>({
    intervalMs: 30_000,
    request: available ? request : undefined,
  });

  // Retry network errors when we come back online
  const whenOnline = useEffectEvent(() => {
    if (state === "error" && isFetchOrAxiosNetworkError(error)) {
      retry({});
    }
  });

  useEffect(() => {
    return onlineManager.subscribe((online) => {
      if (online) {
        whenOnline();
      }
    });
  }, []);

  return useMemo((): ModuleSession => {
    if (error instanceof ModuleSessionConflict) {
      return {
        state: "conflict",
        openUser: error.openUser,
        error,
        retry,
      };
    }
    return {
      state,
      openUser: null,
      error,
      retry,
    };
  }, [state, error, retry]);
};

export const useUpdateModule = ({ onError, onSuccess }: MutationOptions = {}) => {
  const { update } = useModuleStore();
  const queryClient = useQueryClient();

  const {
    mutate: updateModule,
    mutateAsync: updateModuleAsync,
    status,
  } = useMutation({
    mutationFn: update,
    onMutate: async (module) => {
      const queryKey = ["module", module.id];

      await queryClient.cancelQueries({ queryKey });

      const queryData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, module);

      return { queryData };
    },
    onSettled: async (_, error, module, context) => {
      const queryKey = ["module", module.id];

      if (error) {
        if (context) queryClient.setQueryData(queryKey, context.queryData);
        onError?.(error);
      } else {
        await queryClient.invalidateQueries(invalidateModuleListQueryOptions);
        onSuccess?.();
      }

      await queryClient.invalidateQueries({ queryKey });
    },
  });

  return useMemo(() => ({ updateModule, updateModuleAsync, status }), [status, updateModule, updateModuleAsync]);
};

type AddModuleOptions = MutationOptions & {
  navigateOnCreate?: boolean;
};

export const useAddModule = ({ navigateOnCreate, onError, onSuccess }: AddModuleOptions = {}) => {
  const { add } = useModuleStore();
  const queryClient = useQueryClient();

  const {
    mutate: addModule,
    mutateAsync: addModuleAsync,
    status,
  } = useMutation({
    mutationFn: add,
    onSuccess: async (_, variables) => {
      onSuccess?.();
      await queryClient.invalidateQueries(invalidateModuleListQueryOptions);
      if (navigateOnCreate) {
        // NOTE: we are using page refresh to clear any stale data or errors.
        window.location.pathname = `/modules/${variables.id}`;
      }
    },
    onError: (error) => onError?.(error),
  });

  return useMemo(() => ({ addModule, addModuleAsync, status }), [addModule, addModuleAsync, status]);
};

export const useRemoveModule = ({ onError, onSuccess }: MutationOptions = {}) => {
  const { remove } = useModuleStore();
  const queryClient = useQueryClient();

  const {
    mutate: removeModule,
    mutateAsync: removeModuleAsync,
    status,
  } = useMutation({
    mutationFn: remove,
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: ["module", id] });
      await queryClient.invalidateQueries(invalidateModuleListQueryOptions);
      onSuccess?.();
    },
    onError: (error) => onError?.(error),
  });

  return useMemo(() => ({ removeModule, removeModuleAsync, status }), [removeModule, removeModuleAsync, status]);
};

export const useDuplicateModule = (hostId: string, { onError, onSuccess }: MutationOptions = {}) => {
  const { export: exportModule, import: importModule } = useModuleStore();
  const queryClient = useQueryClient();

  const duplicate = async (metadata: Partial<Omit<InteractModuleListItem, "id" | "defaultLanguage">>) => {
    const transport = await exportModule(hostId);
    await importModule({ ...transport, metadata: { ...transport.metadata, ...metadata } });
  };

  const {
    mutate: duplicateModule,
    mutateAsync: duplicateModuleAsync,
    status,
  } = useMutation({
    mutationFn: duplicate,
    onSuccess: async () => {
      await queryClient.invalidateQueries(invalidateModuleListQueryOptions);
      onSuccess?.();
    },
    onError: (error) => onError?.(error),
  });

  return useMemo(
    () => ({ duplicateModule, duplicateModuleAsync, status }),
    [duplicateModule, duplicateModuleAsync, status],
  );
};

const stripTrackingAttributes = (module: InteractModuleResource): InteractModuleResourceProperties =>
  omit(module, ["createdOn", "createdBy", "updatedOn", "updatedBy"]);
